// src/hooks/expertDashboardHooks/useExpertActivityTracker.ts
import { useState, useEffect, useCallback } from 'react';
import { 
  expertActivityTrackerApi, 
} from '../../api/expertDashboardApis/expert-activity-tracker.api';

import type { 
  ActivityStats, 
  ActivityHistoryItem 
} from '../../api/expertDashboardApis/expert-activity-tracker.api';
import { toast } from 'sonner';

// 🟢 FIXED: Changed limit from 10 to 1 to reduce API payload size
export const useExpertActivityTracker = (expertCode: string | undefined | null, limit: number = 1) => {
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [history, setHistory] = useState<ActivityHistoryItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // 🟢 Enhanced to accept the 'filter' parameter (defaults to 'all')
  const fetchTrackerData = useCallback(async (filter: string = 'all') => {
    if (!expertCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch both endpoints concurrently for faster loading
      const [statsData, historyData] = await Promise.all([
        // 🟢 Pass the filter parameter to the API call
        expertActivityTrackerApi.getStats(expertCode, filter),
        // 🟢 Now strictly requesting 1 item from the API to save bandwidth
        expertActivityTrackerApi.getHistory(expertCode, limit)
      ]);
      
      setStats(statsData);
      setHistory(historyData);
    } catch (err: any) {
      console.error('Failed to load expert tracker data:', err);
      const errorMessage = err.response?.data?.message || 'Failed to load activity stats';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [expertCode, limit]);

  // 🟢 On initial mount, fetch the 'all' time data by default
  useEffect(() => {
    fetchTrackerData('all');
  }, [fetchTrackerData]);

  return { 
    stats, 
    history, 
    loading, 
    error, 
    refresh: fetchTrackerData // Exposing this so you can call it manually if needed
  };
};